import { useState, useEffect, useCallback } from 'react'
import toast from 'react-hot-toast'
import api from '../../services/api'
import SelectorNegocio from '../../components/SelectorNegocio'

/**
 * Proyectos — cotizaciones y entregas de los negocios que venden servicios.
 *
 * El equivalente a Pedidos para quien no vende productos: una tienda cierra con
 * un pedido, una empresa de servicios cotiza un alcance, lo aprueban y lo
 * entrega por fases.
 */

const ESTADOS = ['cotizado', 'aprobado', 'en_desarrollo', 'entregado', 'cancelado']
const LABEL = {
  cotizado: 'Cotizado', aprobado: 'Aprobado', en_desarrollo: 'En desarrollo',
  entregado: 'Entregado', cancelado: 'Cancelado',
}
const COLOR = {
  cotizado:      { background: 'rgba(245,158,11,0.15)', color: '#d97706' },
  aprobado:      { background: 'rgba(59,130,246,0.15)', color: '#2563eb' },
  en_desarrollo: { background: 'rgba(168,85,247,0.15)', color: '#9333ea' },
  entregado:     { background: 'rgba(16,185,129,0.15)', color: '#059669' },
  cancelado:     { background: 'rgba(239,68,68,0.15)',  color: '#dc2626' },
}
// Siguiente paso natural, para avanzar con un clic en vez de abrir un selector
const SIGUIENTE = { cotizado: 'aprobado', aprobado: 'en_desarrollo', en_desarrollo: 'entregado' }

const fmt = (n) => (Number(n) || 0).toLocaleString('es-BO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const fecha = (d) => d ? new Date(d).toLocaleDateString('es-BO', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'

function Tarjeta({ titulo, valor, sub, color }) {
  return (
    <div className="card" style={{ padding: 14, flex: '1 1 150px', minWidth: 140 }}>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>{titulo}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: color || 'var(--text)', marginTop: 2 }}>{valor}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{sub}</div>}
    </div>
  )
}

export default function Proyectos() {
  const [proyectos, setProyectos] = useState([])
  const [resumen, setResumen] = useState(null)
  const [cargando, setCargando] = useState(true)
  const [negocio, setNegocio] = useState('todos')
  const [filtro, setFiltro] = useState('todos')
  const [busqueda, setBusqueda] = useState('')
  const [ver, setVer] = useState(null)
  const [editando, setEditando] = useState(null)
  const [guardando, setGuardando] = useState(null)

  const cargar = useCallback(async () => {
    setCargando(true)
    try {
      const { data } = await api.get('/proyectos', {
        params: {
          unidadNegocioId: negocio,
          ...(filtro !== 'todos' ? { estado: filtro } : {}),
          ...(busqueda ? { q: busqueda } : {}),
        },
      })
      setProyectos(data.proyectos || [])
      setResumen(data.resumen || null)
    } catch (e) {
      toast.error(e.response?.data?.error || 'No se pudieron cargar los proyectos')
    } finally { setCargando(false) }
  }, [negocio, filtro, busqueda])

  useEffect(() => { cargar() }, [cargar])

  const cambiarEstado = async (p, estado) => {
    setGuardando(p.id)
    try {
      const { data } = await api.put(`/proyectos/${p.id}`, { estado })
      setProyectos(prev => prev.map(x => x.id === p.id ? data : x))
      if (ver?.id === p.id) setVer(data)
      toast.success(`${p.codigo} → ${LABEL[estado]}`)
      cargar()
    } catch (e) {
      toast.error(e.response?.data?.error || 'No se pudo actualizar')
    } finally { setGuardando(null) }
  }

  // Edición: se trabaja sobre una copia y se guarda de una vez, para poder
  // cancelar sin dejar el proyecto a medias.
  const abrirEdicion = (p) => setEditando({
    id: p.id, codigo: p.codigo, titulo: p.titulo || '', alcance: p.alcance || '',
    tipoServicio: p.tipoServicio || '', monto: p.monto ?? 0, montoPagado: p.montoPagado ?? 0,
    moneda: p.moneda || 'Bs', notas: p.notas || '',
    fechaEstimadaEntrega: p.fechaEstimadaEntrega ? p.fechaEstimadaEntrega.slice(0, 10) : '',
    fases: [...(p.fases || [])],
  })

  const guardarEdicion = async () => {
    if (!editando.titulo.trim()) { toast.error('El proyecto necesita un título'); return }
    setGuardando(editando.id)
    try {
      const { data } = await api.put(`/proyectos/${editando.id}`, {
        titulo: editando.titulo.trim(),
        alcance: editando.alcance,
        tipoServicio: editando.tipoServicio || null,
        monto: Number(editando.monto) || 0,
        montoPagado: Number(editando.montoPagado) || 0,
        moneda: editando.moneda,
        notas: editando.notas || null,
        fechaEstimadaEntrega: editando.fechaEstimadaEntrega || null,
        fases: editando.fases,
      })
      setProyectos(prev => prev.map(x => x.id === data.id ? data : x))
      if (ver?.id === data.id) setVer(data)
      setEditando(null)
      toast.success('Proyecto actualizado')
      cargar()
    } catch (e) {
      toast.error(e.response?.data?.error || 'No se pudo guardar')
    } finally { setGuardando(null) }
  }

  const eliminar = async (p) => {
    if (!window.confirm(
      `¿Eliminar el proyecto ${p.codigo} — "${p.titulo}"?\n\n` +
      `Se borra definitivamente, con su alcance y sus fases. Si el cliente ya no sigue adelante, ` +
      `es mejor marcarlo como "Cancelado": así queda el registro de lo que pasó.`
    )) return
    try {
      await api.delete(`/proyectos/${p.id}`)
      setProyectos(prev => prev.filter(x => x.id !== p.id))
      if (ver?.id === p.id) setVer(null)
      toast.success('Proyecto eliminado')
      cargar()
    } catch (e) { toast.error(e.response?.data?.error || 'No se pudo eliminar') }
  }

  const marcarFase = async (p, i) => {
    const fases = (p.fases || []).map((f, k) => k !== i ? f : {
      ...f,
      estado: f.estado === 'hecha' ? 'pendiente' : 'hecha',
      fecha: f.estado === 'hecha' ? null : new Date().toISOString(),
    })
    try {
      const { data } = await api.put(`/proyectos/${p.id}`, { fases })
      setProyectos(prev => prev.map(x => x.id === p.id ? data : x))
      if (ver?.id === p.id) setVer(data)
    } catch (e) { toast.error('No se pudo actualizar la fase') }
  }

  return (
    <div style={{ padding: 20, maxWidth: 1400, margin: '0 auto' }}>
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>📐 Proyectos y cotizaciones</h1>
        <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
          Lo que vendes como servicio: se cotiza, se aprueba y se entrega por fases.
          El bot deja aquí las cotizaciones que cierra por WhatsApp.
        </p>
      </div>

      <div style={{ marginBottom: 16 }}>
        <SelectorNegocio valor={negocio} onChange={setNegocio} clave="proyectos" rubro="servicios" />
      </div>

      {resumen && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
          <Tarjeta titulo="Proyectos" valor={resumen.total} sub={`${resumen.porEstado.en_desarrollo || 0} en desarrollo`} />
          <Tarjeta titulo="Cotizado" valor={resumen.porEstado.cotizado || 0} sub="esperando respuesta" color="#d97706" />
          <Tarjeta titulo="Valor total" valor={fmt(resumen.montoTotal)} sub="sin contar cancelados" />
          <Tarjeta titulo="Por cobrar" valor={fmt(resumen.porCobrar)} sub={`cobrado ${fmt(resumen.montoPagado)}`} color="#dc2626" />
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14, alignItems: 'center' }}>
        {['todos', ...ESTADOS].map(e => (
          <button key={e} type="button" onClick={() => setFiltro(e)} className="btn"
            style={{
              padding: '6px 12px', fontSize: 13, borderRadius: 999,
              background: filtro === e ? 'var(--text)' : 'var(--bg-card)',
              color: filtro === e ? 'var(--bg-card)' : 'var(--text-muted)',
              border: '1px solid var(--border)',
            }}>
            {e === 'todos' ? 'Todos' : LABEL[e]}
            {resumen && e !== 'todos' ? ` (${resumen.porEstado[e] || 0})` : ''}
          </button>
        ))}
        <input className="input" placeholder="Buscar por código, título o servicio…" value={busqueda}
          onChange={e => setBusqueda(e.target.value)} style={{ flex: 1, minWidth: 200, maxWidth: 320 }} />
      </div>

      {cargando ? (
        <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Cargando…</div>
      ) : !proyectos.length ? (
        <div className="card" style={{ padding: 40, textAlign: 'center' }}>
          <div style={{ fontSize: 34, marginBottom: 8 }}>📐</div>
          <p style={{ fontWeight: 600, marginBottom: 6 }}>Todavía no hay proyectos</p>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', maxWidth: 460, margin: '0 auto' }}>
            Aparecerán aquí cuando el bot cierre una cotización por WhatsApp, o cuando marques
            como ganado un negocio del Pipeline. También puedes crearlos a mano desde la ficha de un contacto.
          </p>
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, minWidth: 820 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)', textAlign: 'left', color: 'var(--text-muted)', fontSize: 12 }}>
                <th style={{ padding: 10 }}>Código</th>
                <th style={{ padding: 10 }}>Proyecto</th>
                <th style={{ padding: 10 }}>Cliente</th>
                {negocio === 'todos' && <th style={{ padding: 10 }}>Negocio</th>}
                <th style={{ padding: 10 }}>Monto</th>
                <th style={{ padding: 10 }}>Estado</th>
                <th style={{ padding: 10 }}>Fecha</th>
                <th style={{ padding: 10 }}></th>
              </tr>
            </thead>
            <tbody>
              {proyectos.map(p => (
                <tr key={p.id} style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                  onClick={() => setVer(p)}>
                  <td style={{ padding: 10, fontFamily: 'monospace', fontSize: 12 }}>{p.codigo}</td>
                  <td style={{ padding: 10 }}>
                    <div style={{ fontWeight: 600 }}>{p.titulo}</div>
                    {p.tipoServicio && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{p.tipoServicio}</div>}
                  </td>
                  <td style={{ padding: 10 }}>{p.Contacto?.nombre || '—'}</td>
                  {negocio === 'todos' && (
                    <td style={{ padding: 10, fontSize: 12 }}>
                      {p.UnidadNegocio ? `${p.UnidadNegocio.emoji || ''} ${p.UnidadNegocio.nombre}` : '—'}
                    </td>
                  )}
                  <td style={{ padding: 10, fontWeight: 600, whiteSpace: 'nowrap' }}>{p.moneda} {fmt(p.monto)}</td>
                  <td style={{ padding: 10 }}>
                    <span style={{ ...COLOR[p.estado], padding: '3px 9px', borderRadius: 999, fontSize: 12, fontWeight: 600 }}>
                      {LABEL[p.estado]}
                    </span>
                  </td>
                  <td style={{ padding: 10, fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                    {fecha(p.fechaCotizacion || p.createdAt)}
                  </td>
                  <td style={{ padding: 10 }} onClick={e => e.stopPropagation()}>
                    {SIGUIENTE[p.estado] && (
                      <button className="btn btn-outline" disabled={guardando === p.id}
                        style={{ padding: '4px 10px', fontSize: 12, whiteSpace: 'nowrap' }}
                        onClick={() => cambiarEstado(p, SIGUIENTE[p.estado])}>
                        → {LABEL[SIGUIENTE[p.estado]]}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {ver && (
        <div className="modal-overlay" onClick={() => setVer(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 620 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
              <div>
                <div style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--text-muted)' }}>{ver.codigo}</div>
                <h2 className="modal-title" style={{ margin: 0 }}>{ver.titulo}</h2>
                {ver.origen === 'bot' && (
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>🤖 Cotizado por el bot en WhatsApp</div>
                )}
              </div>
              <button className="btn" onClick={() => setVer(null)} aria-label="Cerrar"
                style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(130px,1fr))', gap: 12, marginBottom: 14 }}>
              <div><p className="text-muted" style={{ fontSize: 11 }}>Cliente</p><p>{ver.Contacto?.nombre || '—'}</p></div>
              <div><p className="text-muted" style={{ fontSize: 11 }}>Teléfono</p><p>{ver.Contacto?.telefono || '—'}</p></div>
              <div><p className="text-muted" style={{ fontSize: 11 }}>Monto</p><p style={{ fontWeight: 700 }}>{ver.moneda} {fmt(ver.monto)}</p></div>
              <div><p className="text-muted" style={{ fontSize: 11 }}>Cobrado</p><p>{ver.moneda} {fmt(ver.montoPagado)}</p></div>
              <div><p className="text-muted" style={{ fontSize: 11 }}>Cotizado el</p><p>{fecha(ver.fechaCotizacion)}</p></div>
              <div><p className="text-muted" style={{ fontSize: 11 }}>Entregado el</p><p>{fecha(ver.fechaEntrega)}</p></div>
            </div>

            {ver.alcance && (
              <div style={{ marginBottom: 14 }}>
                <p className="text-muted" style={{ fontSize: 11, marginBottom: 4 }}>Alcance acordado</p>
                <div style={{ fontSize: 13, whiteSpace: 'pre-wrap', background: 'var(--bg-hover)', padding: 10, borderRadius: 8 }}>
                  {ver.alcance}
                </div>
              </div>
            )}

            {!!(ver.fases || []).length && (
              <div style={{ marginBottom: 14 }}>
                <p className="text-muted" style={{ fontSize: 11, marginBottom: 6 }}>Fases de entrega</p>
                {ver.fases.map((f, i) => (
                  <label key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', cursor: 'pointer', fontSize: 13 }}>
                    <input type="checkbox" checked={f.estado === 'hecha'} onChange={() => marcarFase(ver, i)} />
                    <span style={{ textDecoration: f.estado === 'hecha' ? 'line-through' : 'none', opacity: f.estado === 'hecha' ? 0.6 : 1 }}>
                      {f.nombre}
                    </span>
                    {f.fecha && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{fecha(f.fecha)}</span>}
                  </label>
                ))}
              </div>
            )}

            {ver.notas && (
              <div style={{ marginBottom: 14 }}>
                <p className="text-muted" style={{ fontSize: 11, marginBottom: 4 }}>Notas</p>
                <div style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{ver.notas}</div>
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', borderTop: '1px solid var(--border)', paddingTop: 12 }}>
              {ESTADOS.map(e => (
                <button key={e} className="btn" disabled={guardando === ver.id || ver.estado === e}
                  onClick={() => cambiarEstado(ver, e)}
                  style={{
                    padding: '6px 12px', fontSize: 13, borderRadius: 8,
                    ...(ver.estado === e ? COLOR[e] : {}),
                    border: '1px solid var(--border)',
                    opacity: ver.estado === e ? 1 : 0.75,
                  }}>{LABEL[e]}</button>
              ))}
            </div>

            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button className="btn btn-primary" style={{ flex: 1 }} onClick={() => abrirEdicion(ver)}>
                ✏️ Editar proyecto
              </button>
              <button className="btn" style={{ color: '#dc2626', borderColor: '#fca5a5' }} onClick={() => eliminar(ver)}>
                🗑️ Eliminar
              </button>
            </div>
          </div>
        </div>
      )}

      {editando && (
        <div className="modal-overlay" onClick={() => setEditando(null)}>
          <div className="modal" style={{ maxWidth: 620 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">Editar {editando.codigo}</span>
              <button className="btn btn-ghost btn-icon" onClick={() => setEditando(null)}>✕</button>
            </div>
            <div className="modal-body" style={{ maxHeight: '68vh', overflowY: 'auto' }}>
              <label className="form-label">Título</label>
              <input className="input" value={editando.titulo}
                onChange={e => setEditando(c => ({ ...c, titulo: e.target.value }))} style={{ marginBottom: 14 }} />

              <label className="form-label">Tipo de servicio</label>
              <input className="input" value={editando.tipoServicio} placeholder="Sistema web, automatización, chatbot…"
                onChange={e => setEditando(c => ({ ...c, tipoServicio: e.target.value }))} style={{ marginBottom: 14 }} />

              <label className="form-label">Alcance acordado</label>
              <textarea className="input" rows={4} value={editando.alcance}
                placeholder="Qué incluye exactamente el trabajo"
                onChange={e => setEditando(c => ({ ...c, alcance: e.target.value }))} style={{ marginBottom: 14, resize: 'vertical' }} />

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(130px,1fr))', gap: 12, marginBottom: 14 }}>
                <div>
                  <label className="form-label">Moneda</label>
                  <input className="input" value={editando.moneda}
                    onChange={e => setEditando(c => ({ ...c, moneda: e.target.value }))} />
                </div>
                <div>
                  <label className="form-label">Monto</label>
                  <input className="input" type="number" min="0" step="0.01" value={editando.monto}
                    onChange={e => setEditando(c => ({ ...c, monto: e.target.value }))} />
                </div>
                <div>
                  <label className="form-label">Ya cobrado</label>
                  <input className="input" type="number" min="0" step="0.01" value={editando.montoPagado}
                    onChange={e => setEditando(c => ({ ...c, montoPagado: e.target.value }))} />
                </div>
                <div>
                  <label className="form-label">Entrega estimada</label>
                  <input className="input" type="date" value={editando.fechaEstimadaEntrega}
                    onChange={e => setEditando(c => ({ ...c, fechaEstimadaEntrega: e.target.value }))} />
                </div>
              </div>

              {Number(editando.montoPagado) > Number(editando.monto) && (
                <div className="form-hint" style={{ color: '#d97706', marginBottom: 14 }}>
                  Lo cobrado supera el monto del proyecto. Revisa si el monto cambió.
                </div>
              )}

              <label className="form-label">Fases de entrega</label>
              <div style={{ marginBottom: 10 }}>
                {editando.fases.map((f, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                    <input className="input" value={f.nombre} placeholder="Nombre de la fase"
                      onChange={e => setEditando(c => ({
                        ...c, fases: c.fases.map((x, k) => k === i ? { ...x, nombre: e.target.value } : x),
                      }))} style={{ flex: 1, minWidth: 0 }} />
                    <select className="select" value={f.estado || 'pendiente'} style={{ width: 130 }}
                      onChange={e => setEditando(c => ({
                        ...c, fases: c.fases.map((x, k) => k === i ? { ...x, estado: e.target.value } : x),
                      }))}>
                      <option value="pendiente">Pendiente</option>
                      <option value="en_curso">En curso</option>
                      <option value="hecha">Hecha</option>
                    </select>
                    <button className="btn btn-ghost btn-icon" title="Quitar fase"
                      onClick={() => setEditando(c => ({ ...c, fases: c.fases.filter((_, k) => k !== i) }))}>🗑️</button>
                  </div>
                ))}
                <button className="btn btn-secondary" style={{ width: '100%' }}
                  onClick={() => setEditando(c => ({ ...c, fases: [...c.fases, { nombre: '', estado: 'pendiente', fecha: null }] }))}>
                  + Añadir fase
                </button>
              </div>

              <label className="form-label">Notas internas</label>
              <textarea className="input" rows={3} value={editando.notas}
                onChange={e => setEditando(c => ({ ...c, notas: e.target.value }))} style={{ resize: 'vertical' }} />
            </div>
            <div className="modal-footer">
              <button className="btn" onClick={() => setEditando(null)}>Cancelar</button>
              <button className="btn btn-primary" onClick={guardarEdicion} disabled={guardando === editando.id}>
                {guardando === editando.id ? 'Guardando…' : 'Guardar cambios'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
